import type { ProvenanceDocumentKind } from "@ondo/shared";
import { useId } from "react";
import type { CSSProperties } from "react";

import { EvidencePill } from "./EvidencePill.tsx";
import { ProvenanceEntryForm } from "./ProvenanceEntryForm.tsx";
import { ProvenanceEntryRead } from "./ProvenanceEntryRead.tsx";
import { PROVENANCE_COPY } from "./provenance.constants.ts";
import type {
  DraftEntry,
  EntryRow,
  VerificationIntent,
} from "./provenance.model.ts";

export interface ProvenanceHandlers {
  onToggle: (id: string) => void;
  onSetField: <K extends keyof DraftEntry>(id: string, key: K, value: DraftEntry[K]) => void;
  onAddDocuments: (id: string, files: File[]) => void;
  onSetDocumentKind: (id: string, documentId: string, kind: ProvenanceDocumentKind) => void;
  onRemoveDocument: (id: string, documentId: string) => void;
  /** Opens a document in the viewer. Signing happens here, not before. */
  onOpenDocument: (id: string, documentId: string) => void;
  onAttachOndoId: (id: string, raw: string) => string | null;
  onDetachOndoId: (id: string) => void;
  onRemove: (id: string) => void;
  /**
   * Required now. Whether a withdraw control EXISTS is `read.removal`, which
   * the surface config and the server decided between them — so an optional
   * handler standing in for a capability was a second answer to a question the
   * row model already answers.
   */
  onWithdraw: (id: string) => void;
  /**
   * Required, both of them. An enabled button with no handler behind it is a
   * lie the type should not permit — and every verification button is enabled
   * somewhere now.
   */
  onAct: (id: string, intent: VerificationIntent) => string | null;
  onUnact: (id: string, actId: string) => void;
  /**
   * Taking a LIVE position back, which is not `onWithdraw` above.
   *
   * `onWithdraw` ends an ENTRY — a claim about the work. This ends an
   * ATTESTATION — the reader's own signature on somebody else's claim. They
   * are two different rows in two different tables and only one of them is
   * about the work's history.
   */
  onWithdrawAttestation: (id: string, attestationId: string) => void;
  /**
   * Taking the reader's own COMMENT back — OND-21. A fourth row in a fourth
   * table: not the entry, not a signature on it, not a verdict on a paper
   * beneath it. A comment is context and moves no count, which is why the
   * route for it sat uncalled for two months without anything looking wrong.
   */
  onWithdrawComment: (id: string, commentId: string) => void;
  /**
   * Accepting or rejecting ONE DOCUMENT — OND-62, and a third thing that is
   * neither of the two above.
   *
   * `onWithdraw` ends an entry, `onWithdrawAttestation` ends a signature on the
   * claim, and this takes a position on a single paper beneath it. Three rows
   * in three tables, and the whole point of the ticket is that the middle one
   * stopped having to stand in for this one: rejecting a forged certificate no
   * longer disputes the genuine invoice filed beside it.
   *
   * Returns the problem sentence or null, like `onAct` — a rejection with no
   * reason is refused before the server sees it.
   */
  onJudgeDocument: (
    id: string,
    documentId: string,
    stance: "verify" | "dispute",
    reason: string,
  ) => string | null;
  onUndoDocumentVerdict: (id: string, verdictId: string) => void;
}

/**
 * The chain of title, as a timeline of events.
 *
 * Each link is a card on a rail: what happened and when at the top, the strip
 * saying who stands behind it at the foot, and everything else — the
 * confirmations, the disputes, the comments, the paperwork and the buttons that
 * add to them — behind the one disclosure on that strip. One switch per card,
 * because the question a reader has is "who says so?" and the answer to it is a
 * single list.
 *
 * NOT `<details>/<summary>`, though the codebase uses that twice elsewhere. The
 * difference is who owns the open state: in the FAQ it is nobody's business, and
 * here it is the controller's — adding an entry has to open it, failing
 * validation has to open the one that failed, and opening a recorded card is
 * what fetches its confirmations. `<details open>` is uncontrolled by default
 * and fights React's value on every click.
 *
 * An `<ol>` so the ordinal is real: the rail mark is decorative and the toggle's
 * accessible name carries "Entry 2 of 4" instead.
 */
export function ProvenanceEntryList({
  rows,
  openEntryId,
  refusals,
  handlers,
}: {
  rows: EntryRow[];
  openEntryId: string | null;
  /** Why documents just dropped on one entry were refused. */
  refusals: { entryId: string; sentences: string[] } | null;
  handlers: ProvenanceHandlers;
}) {
  if (rows.length === 0) {
    return <p className="provenance__empty">{PROVENANCE_COPY.entriesEmpty}</p>;
  }

  return (
    <ol className="provenance__events">
      {rows.map((row) => (
        <EntryEvent
          key={row.id}
          row={row}
          open={row.id === openEntryId}
          refusals={refusals?.entryId === row.id ? refusals.sentences : []}
          handlers={handlers}
        />
      ))}
    </ol>
  );
}

function EntryEvent({
  row,
  open,
  refusals,
  handlers,
}: {
  row: EntryRow;
  open: boolean;
  refusals: string[];
  handlers: ProvenanceHandlers;
}) {
  const base = useId();
  const card = row.card;

  return (
    <li
      className="provenance__event"
      style={
        {
          "--event": `var(--event-${card.tone})`,
          "--event-bg": `var(--event-${card.tone}-bg)`,
          "--grade": card.pill.token,
          "--grade-bg": card.pill.bgToken,
        } as CSSProperties
      }
    >
      <span className="provenance__mark" aria-hidden="true">
        {card.glyph}
      </span>

      <article className="provenance__card">
        <div className="provenance__card-head">
          <h3 className="provenance__card-title">
            {card.action}
            <span className="provenance__card-type">{card.typeLabel}</span>
          </h3>
          <span className="provenance__card-dates">{card.dates}</span>
        </div>

        <p className="provenance__card-owner">{card.ownerName}</p>
        {card.detail !== "" && <p className="provenance__card-detail">{card.detail}</p>}
        {card.notes !== null && <p className="provenance__card-notes">{card.notes}</p>}

        <button
          type="button"
          id={`${base}-head`}
          className={`provenance__strip provenance__strip--${card.strip.tone}`}
          aria-expanded={open}
          aria-controls={`${base}-body`}
          onClick={() => handlers.onToggle(row.id)}
        >
          <span className="provenance__sr">{row.header.label}</span>
          <span className="provenance__stamp">{card.strip.pill}</span>
          <span className="provenance__strip-summary">{card.strip.summary}</span>
          <EvidencePill pill={card.pill} />
          <span className="provenance__docs-count">{card.documentCount}</span>
          <span className="provenance__chevron" aria-hidden="true">
            {open ? "▲" : "▼"}
          </span>
        </button>

        {open && (
          <div id={`${base}-body`} role="group" aria-labelledby={`${base}-head`}>
            {row.state === "draft" ? (
              <ProvenanceEntryForm
                base={base}
                form={row.form}
                refusals={refusals}
                onSetField={(key, value) => handlers.onSetField(row.id, key, value)}
                onAddDocuments={(files) => handlers.onAddDocuments(row.id, files)}
                onSetDocumentKind={(documentId, kind) =>
                  handlers.onSetDocumentKind(row.id, documentId, kind)
                }
                onOpenDocument={(documentId) => handlers.onOpenDocument(row.id, documentId)}
                onRemoveDocument={(documentId) => handlers.onRemoveDocument(row.id, documentId)}
                onAttachOndoId={(raw) => handlers.onAttachOndoId(row.id, raw)}
                onDetachOndoId={() => handlers.onDetachOndoId(row.id)}
                onRemove={() => handlers.onRemove(row.id)}
                onAct={(intent) => handlers.onAct(row.id, intent)}
                onUnact={(actId) => handlers.onUnact(row.id, actId)}
              />
            ) : (
              <ProvenanceEntryRead
                read={row.read}
                onAct={(intent) => handlers.onAct(row.id, intent)}
                onUnact={(actId) => handlers.onUnact(row.id, actId)}
                onWithdraw={() => handlers.onWithdraw(row.id)}
                onWithdrawAttestation={(attestationId) =>
                  handlers.onWithdrawAttestation(row.id, attestationId)
                }
                onWithdrawComment={(commentId) => handlers.onWithdrawComment(row.id, commentId)}
                onOpenDocument={(documentId) => handlers.onOpenDocument(row.id, documentId)}
                onRemoveDocument={(documentId) => handlers.onRemoveDocument(row.id, documentId)}
                onJudgeDocument={(documentId, stance, reason) =>
                  handlers.onJudgeDocument(row.id, documentId, stance, reason)
                }
                onUndoDocumentVerdict={(verdictId) =>
                  handlers.onUndoDocumentVerdict(row.id, verdictId)
                }
              />
            )}
          </div>
        )}
      </article>
    </li>
  );
}
